<template>
  <BlockFieldset class="project-calculation-internal">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <div v-else>
      <b-row>
        <b-col xl="6" lg="6" md="12">
          <b-form-group
            horizontal
            :label="$t('calculation_vatPercentageState')"
            label-for="vatPercentageState"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <Dropdown
              :disabled="isBlockReadOnly"
              v-model="project.vatPercentageState"
              :options="vatPercentageOptions"
              :placeholder="$t('unit_percent')"
              optionLabel="label"
              optionValue="value"
              :showClear="true"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('calculation_generalBusinessCosts')"
            label-for="generalBusinessCosts"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <UnitInputNumber
              id="generalBusinessCosts"
              v-model="displayedGeneralBusinessCosts"
              unit="unit_percent"
              :disabled="isBlockReadOnly || !overrideGeneralBusinessCosts"
            />
            <small v-if="generalBusinessCostsSettingsHint" class="text-muted">
              {{ generalBusinessCostsSettingsHint }}
            </small>
          </b-form-group>
          <b-form-group
            horizontal
            label-for="generalBusinessCostsOverridden"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-checkbox
              :checked="overrideGeneralBusinessCosts"
              id="generalBusinessCostsOverridden"
              @change="onGeneralBusinessCostsOverrideChange"
            >
              {{ $t('generalBusinessCostsOverridden') }}
            </b-form-checkbox>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('calculation_profitMargin')"
            label-for="profitMargin"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <UnitInputNumber
              id="profitMargin"
              v-model="displayedProfitMargin"
              unit="unit_percent"
              :disabled="isBlockReadOnly || !overrideProfitMargin"
            />
          </b-form-group>
          <b-form-group
            horizontal
            label-for="profitMarginOverridden"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-checkbox
              :checked="overrideProfitMargin"
              id="profitMarginOverridden"
              @change="onProfitMarginOverrideChange"
            >
              {{ $t('profitMarginOverridden') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col xl="6" lg="6" md="12">
          <b-form-group
            horizontal
            :label="$t('costCalculationBasis')"
            label-for="costCalculationBasis"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <multiselect
              :disabled="isBlockReadOnly"
              :placeholder="$t('costCalculationBasis')"
              v-model="project.costCalculationBasis"
              id="costCalculationBasis"
              :options="getEnumValues('CostCalculationBasis')"
              :allow-empty="false"
              :show-labels="false"
              deselectLabel
            >
              <template slot="singleLabel" slot-scope="{ option }">
                {{ $t(option) }}
              </template>
              <template slot="option" slot-scope="{ option }">
                {{ $t(option) }}
              </template>
            </multiselect>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('calculatedExternalCost')"
            label-for="calculatedExternalCost"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <UnitInputNumber
              v-model="project.calculatedExternalCost"
              unit="unit_eur"
              :disabled="isBlockReadOnly || project.costCalculationBasis === 'ONLY_INTERNAL_COST'"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('projectRightsCost')"
            label-for="projectRightsCost"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <UnitInputNumber
              id="projectRightsCost"
              v-model="project.projectRightsCost"
              unit="unit_eur"
              :disabled="isBlockReadOnly || isOnlyExternalCostBasis"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('oneTimeLeaseCostDriver')"
            label-for="oneTimeLeaseCostDriver"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <SelectButton
              id="oneTimeLeaseCostDriver"
              v-model="oneTimeLeaseCostDriver"
              class="calculation-mode-select"
              :options="leaseDriverOptions"
              :disabled="isBlockReadOnly || isOnlyExternalCostBasis"
            >
              <template #option="props">
                {{ $t(props.option) }}
              </template>
            </SelectButton>
          </b-form-group>

          <b-form-group
            v-if="isLeasePerSquareMeterDriver"
            horizontal
            :label="$t('oneTimeLeaseCostPerSquareMeter')"
            label-for="oneTimeLeaseCostPerSquareMeter"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <UnitInputNumber
              id="oneTimeLeaseCostPerSquareMeter"
              v-model="project.oneTimeLeaseCostPerSquareMeter"
              unit="unit_eur_per_square_meter"
              :minFractionDigits="2"
              :maxFractionDigits="4"
              :disabled="isBlockReadOnly || isOnlyExternalCostBasis"
            />
          </b-form-group>

          <b-form-group
            v-else
            horizontal
            :label="$t('oneTimeLeaseCostPerkWp')"
            label-for="oneTimeLeaseCostPerkWp"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <UnitInputNumber
              id="oneTimeLeaseCostPerkWp"
              v-model="project.oneTimeLeaseCostPerkWp"
              unit="unit_eur_per_kwp"
              :minFractionDigits="2"
              :maxFractionDigits="4"
              :disabled="isBlockReadOnly || isOnlyExternalCostBasis"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('effectiveProjectLeaseCost')"
            label-for="effectiveProjectLeaseCost"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <div class="form-control-plaintext">
              {{
                formatNumberWithUnit(effectiveProjectLeaseCost, NUMBER_FORMAT.DECIMAL, 'unit_eur')
              }}
            </div>
            <small class="text-muted">
              {{ effectiveProjectLeaseCostFormulaHint }}
            </small>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('additionalProfitMargin')"
            label-for="additionalProfitMargin"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <UnitInputNumber
              :disabled="isBlockReadOnly"
              id="additionalProfitMargin"
              v-model="project.additionalProfitMargin"
              unit="unit_percent"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('baseSettlementValuePerkWp')"
            label-for="baseSettlementValuePerkWp"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <UnitInputNumber
              :disabled="isBlockReadOnly"
              v-model="project.baseSettlementValuePerkWp"
              unit="unit_eur_per_kwp"
              :minFractionDigits="2"
              :maxFractionDigits="4"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('baseSettlementValue')"
            label-for="baseSettlementValueMode"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <SelectButton
              :disabled="isBlockReadOnly"
              id="baseSettlementValueMode"
              v-model="baseSettlementValueMode"
              class="calculation-mode-select"
              :options="automaticManualOptions"
            >
              <template #option="props">
                {{ $t(props.option) }}
              </template>
            </SelectButton>

            <UnitInputNumber
              :disabled="isBlockReadOnly"
              v-if="isBaseSettlementValueManual"
              v-model="displayedBaseSettlementValue"
              unit="unit_eur"
            />
            <div v-else class="form-control-plaintext">
              {{
                formatNumberWithUnit(
                  displayedBaseSettlementValue,
                  NUMBER_FORMAT.DECIMAL,
                  'unit_eur'
                )
              }}
            </div>
            <small v-if="!isBaseSettlementValueManual" class="text-muted">
              {{ baseSettlementValueFormulaHint }}
            </small>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('commissionPercentage')"
            label-for="commissionPercentageMode"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <SelectButton
              :disabled="isBlockReadOnly"
              id="commissionPercentageMode"
              v-model="commissionPercentageMode"
              class="calculation-mode-select"
              :options="automaticManualOptions"
            >
              <template #option="props">
                {{ $t(props.option) }}
              </template>
            </SelectButton>

            <UnitInputNumber
              :disabled="isBlockReadOnly"
              v-if="isCommissionPercentageManual"
              v-model="displayedCommissionPercentage"
              unit="unit_percent"
              :minFractionDigits="2"
              :maxFractionDigits="5"
            />
            <div v-else class="form-control-plaintext">
              {{
                formatNumberWithUnit(
                  displayedCommissionPercentage,
                  NUMBER_FORMAT.DECIMAL_QUINQUIPLE,
                  'unit_percent'
                )
              }}
            </div>
            <small v-if="!isCommissionPercentageManual" class="text-muted">
              {{ commissionPercentageFormulaHint }}
            </small>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('calculatedProvision')"
            label-for="calculatedProvisionMode"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <SelectButton
              :disabled="isBlockReadOnly"
              id="calculatedProvisionMode"
              v-model="calculatedProvisionMode"
              class="calculation-mode-select"
              :options="automaticManualOptions"
            >
              <template #option="props">
                {{ $t(props.option) }}
              </template>
            </SelectButton>

            <UnitInputNumber
              :disabled="isBlockReadOnly"
              v-if="isCalculatedProvisionManual"
              v-model="displayedCalculatedProvision"
              unit="unit_eur"
            />
            <div v-else class="form-control-plaintext">
              {{
                formatNumberWithUnit(
                  displayedCalculatedProvision,
                  NUMBER_FORMAT.DECIMAL,
                  'unit_eur'
                )
              }}
            </div>
            <small v-if="!isCalculatedProvisionManual" class="text-muted">
              {{ calculatedProvisionFormulaHint }}
            </small>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('calculatedSettlementValueDriver')"
            label-for="calculatedSettlementValueDriver"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <SelectButton
              :disabled="isBlockReadOnly"
              id="calculatedSettlementValueDriver"
              v-model="settlementValueDriver"
              class="calculation-mode-select"
              :options="settlementDriverOptions"
              @input="onSettlementDriverChange"
            >
              <template #option="props">
                {{ $t(props.option) }}
              </template>
            </SelectButton>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('calculatedSettlementValue')"
            label-for="calculatedSettlementValue"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <UnitInputNumber
              :disabled="isBlockReadOnly"
              v-if="isManualTotalDriver"
              v-model="project.calculatedSettlementValue"
              unit="unit_eur"
            />
            <div v-else class="form-control-plaintext d-flex align-items-center">
              {{
                formatNumberWithUnit(
                  appliedCalculatedSettlementValue,
                  NUMBER_FORMAT.DECIMAL,
                  'unit_eur'
                )
              }}
            </div>
            <small v-if="isManualPerKwpDriver" class="text-muted">
              {{ settlementValueDerivedFromPerKwpHint }}
            </small>
            <small v-else-if="isAutoDriver" class="text-muted">
              {{ calculatedSettlementValueFormulaHint }}
            </small>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('calculatedSettlementValuePerkWp')"
            label-for="calculatedSettlementValuePerkWp"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <UnitInputNumber
              :disabled="isBlockReadOnly"
              v-if="isManualPerKwpDriver"
              v-model="project.calculatedSettlementValuePerkWp"
              unit="unit_eur_per_kwp"
              :minFractionDigits="2"
              :maxFractionDigits="4"
            />
            <div v-else class="form-control-plaintext d-flex align-items-center">
              {{
                formatNumberWithUnit(
                  appliedCalculatedSettlementValuePerkWp,
                  NUMBER_FORMAT.DECIMAL,
                  'unit_eur_per_kwp'
                )
              }}
            </div>
            <small v-if="isManualTotalDriver" class="text-muted">
              {{ settlementPerKwpDerivedFromTotalHint }}
            </small>
            <small v-else-if="isAutoDriver" class="text-muted">
              {{ calculatedSettlementValuePerkWpFormulaHint }}
            </small>
          </b-form-group>
        </b-col>

        <b-col xl="6" lg="6" md="12" class="mt-3 mt-lg-0">
          <ProjectCalculationBaseReferencePanel
            class="mb-3"
            :cost-calculation-basis="project.costCalculationBasis"
            :solar-plant-overall-power="solarPlantOverallPower"
            :calculated-external-cost="project.calculatedExternalCost"
            :project-rights-cost="project.projectRightsCost"
            :effective-project-lease-cost="effectiveProjectLeaseCost"
            :effective-total-planned-material-cost="storedProject.effectiveTotalPlannedMaterialCost"
            :effective-total-labor-cost="storedProject.effectiveTotalLaborCost"
            :effective-general-business-costs="storedProject.effectiveGeneralBusinessCosts"
            :effective-general-business-costs-percentage="
              storedProject.effectiveGeneralBusinessCostsPercentage
            "
            :effective-risk-and-profit="storedProject.effectiveRiskAndProfit"
            :effective-profit-margin-percentage="storedProject.effectiveProfitMarginPercentage"
            :additional-profit-margin="project.additionalProfitMargin"
          />
        </b-col>
      </b-row>

      <div v-if="hasSaveButton" class="text-right mt-2">
        <b-button variant="bg" :disabled="isSaving" @click="save">
          {{ $t('saveNamed', { name: $t('project_calculation') }) }}
        </b-button>
      </div>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import Dropdown from 'primevue/dropdown';
import SelectButton from 'primevue/selectbutton';
import UnitInputNumber from '@/components/UnitInputNumber';
import { formatNumber, formatNumberWithUnit, NUMBER_FORMAT } from '@/helpers/numberFormat';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import ProjectCalculationBaseReferencePanel from '@/views/Projects/ProjectCalculationBaseReferencePanel.vue';
import {
  DEFAULT_CALCULATED_SETTLEMENT_VALUE_DRIVER,
  DEFAULT_ONE_TIME_LEASE_COST_DRIVER,
  toProjectCalculationInternalInput,
} from './projectCalculationInternalInput';

/** Number of the unsaved project (ProjectEdit `_new`): the block needs a saved project. */
const NEW_PROJECT_NUMBER = '_new';

const SETTLEMENT_DRIVER_AUTO = DEFAULT_CALCULATED_SETTLEMENT_VALUE_DRIVER;
const SETTLEMENT_DRIVER_MANUAL_TOTAL = 'MANUAL_TOTAL';
const SETTLEMENT_DRIVER_MANUAL_PER_KWP = 'MANUAL_PER_KWP';
const LEASE_DRIVER_PER_KWP = DEFAULT_ONE_TIME_LEASE_COST_DRIVER;
const LEASE_DRIVER_PER_SQUARE_METER = 'PER_SQUARE_METER';
const MODE_AUTOMATIC = 'AUTOMATIC';
const MODE_MANUAL = 'MANUAL';

/**
 * Block project-calculation, "Kalkulation – intern" (plan
 * docs/work/_done/2026-10-project-block-calculation-internal): the former sections "Allgemein" (VAT,
 * AGK, W&G) and "Kalkulation" of the tab Intern, moved without business changes and saved through
 * its own mutation. `project` is the block data; plant power, area and the effective values of
 * other parts come from the stored project, so the live calculation follows their changes. On the
 * unsaved project it has no save button. Emits `dirty` and `saved`.
 */
export default {
  name: 'ProjectCalculationInternal',
  components: {
    BlockFieldset,
    Multiselect,
    Dropdown,
    UnitInputNumber,
    SelectButton,
    ProjectCalculationBaseReferencePanel,
  },
  mixins: [AccessBlockMixin, BlockSaveMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
  },
  data() {
    return {
      NUMBER_FORMAT,
      overrideProfitMargin: this.project.profitMargin != null,
      overrideGeneralBusinessCosts: this.project.generalBusinessCosts != null,
      vatPercentageOptions: [
        { value: 19, label: '19 %' },
        { value: 0, label: '0 %' },
      ],
      settlementDriverOptions: [
        SETTLEMENT_DRIVER_AUTO,
        SETTLEMENT_DRIVER_MANUAL_TOTAL,
        SETTLEMENT_DRIVER_MANUAL_PER_KWP,
      ],
      leaseDriverOptions: [LEASE_DRIVER_PER_KWP, LEASE_DRIVER_PER_SQUARE_METER],
      automaticManualOptions: [MODE_AUTOMATIC, MODE_MANUAL],
    };
  },
  computed: {
    ...mapGetters(['getEnumValues', 'getProjectByNumber']),
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    blockInput() {
      return toProjectCalculationInternalInput(this.project);
    },
    hasSaveButton() {
      return !this.isBlockReadOnly && !this.isNewProject;
    },
    /**
     * The project of the page: source of plant power, area and the effective values, which belong
     * to other parts. On `_new` the form is not shown (save-first hint).
     */
    storedProject() {
      return this.getProjectByNumber(this.project.number) || {};
    },

    /** ITEM/SALES defaults from the block type; null fields hide the hints. */
    settingsDefaults() {
      return this.project.settingsDefaults || {};
    },
    displayedProfitMargin: {
      get() {
        if (this.overrideProfitMargin) {
          return this.project.profitMargin;
        }
        return this.settingsDefaults.profitMargin ?? null;
      },
      set(value) {
        if (this.overrideProfitMargin) {
          this.project.profitMargin = value;
        }
      },
    },
    displayedGeneralBusinessCosts: {
      get() {
        if (this.overrideGeneralBusinessCosts) {
          return this.project.generalBusinessCosts;
        }
        return this.settingsDefaults.generalBusinessCosts ?? null;
      },
      set(value) {
        if (this.overrideGeneralBusinessCosts) {
          this.project.generalBusinessCosts = value;
        }
      },
    },
    generalBusinessCostsSettingsHint() {
      if (this.overrideGeneralBusinessCosts) {
        return '';
      }
      const globalValue = this.settingsDefaults.generalBusinessCosts;
      if (globalValue == null) {
        return '';
      }
      return this.$t('calculation_generalBusinessCosts_settingsDefault', {
        value: formatNumberWithUnit(globalValue, NUMBER_FORMAT.DECIMAL, 'unit_percent'),
      });
    },

    /** Plant power of block Basiskonfiguration, read from the stored project. */
    solarPlantOverallPower() {
      return this.storedProject.solarPlant?.solarPlantOverallPower ?? 0;
    },

    /** Area of the IAB data, read from the stored project. */
    areaInSquareMeters() {
      return this.storedProject.investmentDeductionAllowance?.areaInSquareMeters ?? 0;
    },

    isOnlyExternalCostBasis() {
      return this.project.costCalculationBasis === 'ONLY_EXTERNAL_COST';
    },

    oneTimeLeaseCostDriver: {
      get() {
        return this.project.oneTimeLeaseCostDriver || LEASE_DRIVER_PER_KWP;
      },
      set(value) {
        this.project.oneTimeLeaseCostDriver = value;
      },
    },

    isLeasePerKwpDriver() {
      return this.oneTimeLeaseCostDriver === LEASE_DRIVER_PER_KWP;
    },

    isLeasePerSquareMeterDriver() {
      return this.oneTimeLeaseCostDriver === LEASE_DRIVER_PER_SQUARE_METER;
    },

    /**
     * Live preview; mirrors ProjectCalculationService.calculateProjectLeaseCost.
     * project.effectiveProjectLeaseCost from GraphQL updates after save/refetch.
     */
    effectiveProjectLeaseCost() {
      if (this.isOnlyExternalCostBasis) {
        return 0;
      }
      if (this.isLeasePerSquareMeterDriver) {
        const leaseCostPerSquareMeter = this.project.oneTimeLeaseCostPerSquareMeter ?? 0;
        return leaseCostPerSquareMeter * this.areaInSquareMeters;
      }
      const leaseCostPerKwp = this.project.oneTimeLeaseCostPerkWp ?? 0;
      return leaseCostPerKwp * this.solarPlantOverallPower;
    },

    settlementValueDriver: {
      get() {
        return this.project.calculatedSettlementValueDriver || SETTLEMENT_DRIVER_AUTO;
      },
      set(value) {
        this.project.calculatedSettlementValueDriver = value;
      },
    },

    isAutoDriver() {
      return this.settlementValueDriver === SETTLEMENT_DRIVER_AUTO;
    },

    isManualTotalDriver() {
      return this.settlementValueDriver === SETTLEMENT_DRIVER_MANUAL_TOTAL;
    },

    isManualPerKwpDriver() {
      return this.settlementValueDriver === SETTLEMENT_DRIVER_MANUAL_PER_KWP;
    },

    isBaseSettlementValueManual() {
      return this.baseSettlementValueMode === MODE_MANUAL;
    },

    isCommissionPercentageManual() {
      return this.commissionPercentageMode === MODE_MANUAL;
    },

    isCalculatedProvisionManual() {
      return this.calculatedProvisionMode === MODE_MANUAL;
    },

    baseSettlementValueMode: {
      get() {
        return this.project.baseSettlementValue != null ? MODE_MANUAL : MODE_AUTOMATIC;
      },
      set(mode) {
        if (mode === MODE_MANUAL) {
          if (this.project.baseSettlementValue == null) {
            this.project.baseSettlementValue = this.formulaBaseSettlementValue;
          }
        } else {
          this.project.baseSettlementValue = null;
        }
      },
    },

    commissionPercentageMode: {
      get() {
        return this.project.commissionPercentage != null ? MODE_MANUAL : MODE_AUTOMATIC;
      },
      set(mode) {
        if (mode === MODE_MANUAL) {
          if (this.project.commissionPercentage == null) {
            this.project.commissionPercentage = this.settingsDefaults.commissionPercentage ?? null;
          }
        } else {
          this.project.commissionPercentage = null;
        }
      },
    },

    calculatedProvisionMode: {
      get() {
        return this.project.calculatedProvision != null ? MODE_MANUAL : MODE_AUTOMATIC;
      },
      set(mode) {
        if (mode === MODE_MANUAL) {
          if (this.project.calculatedProvision == null) {
            this.project.calculatedProvision =
              (this.effectiveBaseSettlementValue * this.effectiveCommissionPercent) / 100 || 0;
          }
        } else {
          this.project.calculatedProvision = null;
        }
      },
    },

    effectiveCommissionPercent() {
      if (this.project.commissionPercentage != null) {
        return this.project.commissionPercentage;
      }
      return this.autoCommissionPercentage;
    },

    formulaBaseSettlementValue() {
      const baseSettlementValuePerkWp = this.project.baseSettlementValuePerkWp ?? 0;
      return this.solarPlantOverallPower * baseSettlementValuePerkWp || 0;
    },

    effectiveBaseSettlementValue() {
      if (this.project.baseSettlementValue != null) {
        return this.project.baseSettlementValue;
      }
      return this.formulaBaseSettlementValue;
    },

    autoCommissionPercentage() {
      return this.settingsDefaults.commissionPercentage ?? 0;
    },

    autoCalculatedProvision() {
      return (this.formulaBaseSettlementValue * this.autoCommissionPercentage) / 100 || 0;
    },

    effectiveCalculatedProvision() {
      if (this.project.calculatedProvision != null) {
        return this.project.calculatedProvision;
      }
      return (this.effectiveBaseSettlementValue * this.effectiveCommissionPercent) / 100 || 0;
    },

    displayedCommissionPercentage: {
      get() {
        return this.effectiveCommissionPercent;
      },
      set(value) {
        if (this.project.commissionPercentage != null) {
          this.project.commissionPercentage = value;
        }
      },
    },

    displayedBaseSettlementValue: {
      get() {
        return this.effectiveBaseSettlementValue;
      },
      set(value) {
        if (this.project.baseSettlementValue != null) {
          this.project.baseSettlementValue = value;
        }
      },
    },

    displayedCalculatedProvision: {
      get() {
        return this.effectiveCalculatedProvision;
      },
      set(value) {
        if (this.project.calculatedProvision != null) {
          this.project.calculatedProvision = value;
        }
      },
    },

    autoCalculatedSettlementValue() {
      return this.formulaBaseSettlementValue + this.autoCalculatedProvision;
    },

    effectiveCalculatedSettlementValue() {
      if (this.isManualTotalDriver) {
        return this.project.calculatedSettlementValue ?? 0;
      }
      if (this.isManualPerKwpDriver) {
        const settlementValuePerkWp = this.project.calculatedSettlementValuePerkWp ?? 0;
        return settlementValuePerkWp * this.solarPlantOverallPower;
      }
      return this.effectiveBaseSettlementValue + this.effectiveCalculatedProvision;
    },

    appliedCalculatedSettlementValue() {
      return this.effectiveCalculatedSettlementValue;
    },

    autoCalculatedSettlementValuePerkWp() {
      if (this.solarPlantOverallPower === 0) {
        return 0;
      }
      const value = this.autoCalculatedSettlementValue / this.solarPlantOverallPower;
      return Math.round(value * 100) / 100;
    },

    effectiveCalculatedSettlementValuePerkWp() {
      if (this.isManualPerKwpDriver) {
        return this.project.calculatedSettlementValuePerkWp ?? 0;
      }
      if (this.solarPlantOverallPower === 0) {
        return 0;
      }
      if (this.isManualTotalDriver) {
        const settlementValue = this.project.calculatedSettlementValue ?? 0;
        return settlementValue / this.solarPlantOverallPower;
      }
      return this.effectiveCalculatedSettlementValue / this.solarPlantOverallPower;
    },

    appliedCalculatedSettlementValuePerkWp() {
      return this.effectiveCalculatedSettlementValuePerkWp;
    },

    settlementValueDifferenceToAuto() {
      return this.effectiveCalculatedSettlementValue - this.autoCalculatedSettlementValue;
    },

    settlementPerKwpDifferenceToAuto() {
      return (
        this.effectiveCalculatedSettlementValuePerkWp - this.autoCalculatedSettlementValuePerkWp
      );
    },

    baseSettlementValueDifferenceToAuto() {
      return this.effectiveBaseSettlementValue - this.formulaBaseSettlementValue;
    },

    commissionPercentageDifferenceToAuto() {
      return this.effectiveCommissionPercent - this.autoCommissionPercentage;
    },

    calculatedProvisionDifferenceToAuto() {
      return this.effectiveCalculatedProvision - this.autoCalculatedProvision;
    },

    effectiveProjectLeaseCostFormulaHint() {
      if (this.isLeasePerSquareMeterDriver) {
        const leasePerSquareMeterFormatted = formatNumber(
          this.project.oneTimeLeaseCostPerSquareMeter ?? 0,
          NUMBER_FORMAT.CURRENCY
        );
        const areaFormatted = `${formatNumber(this.areaInSquareMeters, NUMBER_FORMAT.DECIMAL)} m²`;
        return `${this.$t('oneTimeLeaseCostPerSquareMeter')} × ${this.$t(
          'investmentDeductionAllowance_areaInSquareMeters'
        )} (${leasePerSquareMeterFormatted} × ${areaFormatted})`;
      }

      const leasePerKwpFormatted = `${formatNumber(
        this.project.oneTimeLeaseCostPerkWp ?? 0,
        NUMBER_FORMAT.CURRENCY
      )}`;
      const powerFormatted = `${formatNumber(
        this.solarPlantOverallPower,
        NUMBER_FORMAT.DECIMAL
      )} ${this.$t('solarPlantOverallPower_short')}`;
      return `${this.$t('oneTimeLeaseCostPerkWp')} × ${this.$t(
        'solarPlantOverallPower'
      )} (${leasePerKwpFormatted} × ${powerFormatted})`;
    },

    baseSettlementValueFormulaHint() {
      const powerFormatted = `${formatNumber(
        this.solarPlantOverallPower,
        NUMBER_FORMAT.DECIMAL
      )} ${this.$t('solarPlantOverallPower_short')}`;
      const perKwpFormatted = `${formatNumber(
        this.project.baseSettlementValuePerkWp ?? 0,
        NUMBER_FORMAT.CURRENCY
      )}`;
      return `${this.$t('baseSettlementValue')}: ${this.$t('solarPlantOverallPower')} × ${this.$t(
        'baseSettlementValuePerkWp'
      )} (${powerFormatted} x ${perKwpFormatted})`;
    },

    commissionPercentageFormulaHint() {
      const globalValue = this.settingsDefaults.commissionPercentage;
      if (globalValue != null) {
        return `${this.$t('totalCommission')}: ${formatNumber(
          globalValue / 100,
          NUMBER_FORMAT.PERCENT_FIVE_DECIMALS
        )} aus Einstellungen`;
      }
      return this.$t('totalCommission');
    },

    calculatedProvisionFormulaHint() {
      const baseSettlementValueFormatted = formatNumber(
        this.displayedBaseSettlementValue,
        NUMBER_FORMAT.CURRENCY
      );
      const commissionFormatted = formatNumber(
        this.effectiveCommissionPercent / 100,
        NUMBER_FORMAT.PERCENT
      );
      return `${this.$t('calculatedProvision')}: ${this.$t('baseSettlementValue')} × ${this.$t(
        'commissionPercentage'
      )} (${baseSettlementValueFormatted} x ${commissionFormatted})`;
    },

    calculatedSettlementValueFormulaHint() {
      const baseSettlementValueFormatted = formatNumber(
        this.displayedBaseSettlementValue,
        NUMBER_FORMAT.CURRENCY
      );
      const provisionFormatted = formatNumber(
        this.displayedCalculatedProvision,
        NUMBER_FORMAT.CURRENCY
      );
      return `${this.$t('calculatedSettlementValue')}: ${this.$t(
        'baseSettlementValue'
      )} + ${this.$t(
        'calculatedProvision'
      )} (${baseSettlementValueFormatted} + ${provisionFormatted})`;
    },

    calculatedSettlementValuePerkWpFormulaHint() {
      const settlementValueFormatted = formatNumber(
        this.appliedCalculatedSettlementValue,
        NUMBER_FORMAT.CURRENCY
      );
      const powerFormatted = `${formatNumber(
        this.solarPlantOverallPower,
        NUMBER_FORMAT.DECIMAL
      )} ${this.$t('solarPlantOverallPower_short')}`;
      return `${this.$t('calculatedSettlementValuePerkWp')}: ${this.$t(
        'calculatedSettlementValue'
      )} ÷ ${this.$t('solarPlantOverallPower')} (${settlementValueFormatted} ÷ ${powerFormatted})`;
    },

    settlementValueDerivedFromPerKwpHint() {
      const perKwpFormatted = `${formatNumber(
        this.project.calculatedSettlementValuePerkWp ?? 0,
        NUMBER_FORMAT.CURRENCY
      )}`;
      const powerFormatted = `${formatNumber(
        this.solarPlantOverallPower,
        NUMBER_FORMAT.DECIMAL
      )} ${this.$t('solarPlantOverallPower_short')}`;
      return `${this.$t('calculatedSettlementValue')}: ${this.$t(
        'calculatedSettlementValuePerkWp'
      )} × ${this.$t('solarPlantOverallPower')} (${perKwpFormatted} x ${powerFormatted})`;
    },

    settlementPerKwpDerivedFromTotalHint() {
      const settlementValueFormatted = formatNumber(
        this.project.calculatedSettlementValue ?? 0,
        NUMBER_FORMAT.CURRENCY
      );
      const powerFormatted = `${formatNumber(
        this.solarPlantOverallPower,
        NUMBER_FORMAT.DECIMAL
      )} ${this.$t('solarPlantOverallPower_short')}`;
      return `${this.$t('calculatedSettlementValuePerkWp')}: ${this.$t(
        'calculatedSettlementValue'
      )} ÷ ${this.$t('solarPlantOverallPower')} (${settlementValueFormatted} ÷ ${powerFormatted})`;
    },
  },
  watch: {
    // Runs after the baseline of BlockSaveMixin, which already uses the same default drivers.
    project: {
      handler: 'initDrivers',
      immediate: true,
    },
  },
  methods: {
    ...mapActions(['updateProjectCalculationInternal', 'fetchEnumValues']),
    formatNumber,
    formatNumberWithUnit,
    saveBlockInput(input) {
      return this.updateProjectCalculationInternal({ projectNumber: this.project.number, input });
    },
    /** Missing drivers get the defaults, as ProjectCalculationAdvanced set them on mount. */
    initDrivers() {
      if (!this.project.calculatedSettlementValueDriver) {
        this.$set(this.project, 'calculatedSettlementValueDriver', SETTLEMENT_DRIVER_AUTO);
      }
      if (!this.project.oneTimeLeaseCostDriver) {
        this.$set(this.project, 'oneTimeLeaseCostDriver', LEASE_DRIVER_PER_KWP);
      }
    },

    onProfitMarginOverrideChange(eventOrChecked) {
      if (eventOrChecked) {
        this.overrideProfitMargin = true;
      } else {
        this.overrideProfitMargin = false;
        this.project.profitMargin = null;
      }
    },

    onGeneralBusinessCostsOverrideChange(eventOrChecked) {
      if (eventOrChecked) {
        this.overrideGeneralBusinessCosts = true;
      } else {
        this.overrideGeneralBusinessCosts = false;
        this.project.generalBusinessCosts = null;
      }
    },

    onSettlementDriverChange(newDriver) {
      if (
        newDriver === SETTLEMENT_DRIVER_MANUAL_TOTAL &&
        this.project.calculatedSettlementValue == null
      ) {
        this.project.calculatedSettlementValue =
          this.effectiveBaseSettlementValue + this.effectiveCalculatedProvision;
      }
      if (
        newDriver === SETTLEMENT_DRIVER_MANUAL_PER_KWP &&
        this.project.calculatedSettlementValuePerkWp == null
      ) {
        if (this.solarPlantOverallPower === 0) {
          this.project.calculatedSettlementValuePerkWp = 0;
        } else {
          this.project.calculatedSettlementValuePerkWp =
            (this.effectiveBaseSettlementValue + this.effectiveCalculatedProvision) /
            this.solarPlantOverallPower;
        }
      }
    },
  },
  async created() {
    await Promise.all([
      this.fetchEnumValues('CostCalculationBasis'),
      this.fetchEnumValues('CalculatedSettlementValueDriver'),
    ]);
  },
};
</script>

<style scoped lang="scss">
::v-deep .calculation-mode-select.p-selectbutton .p-button {
  padding: 0.1rem 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
}
</style>
